<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
      @click.self="$emit('close')"
    >
      <div class="absolute inset-0 bg-black/30 backdrop-blur-sm" @click="$emit('close')" />

      <div class="relative bg-white rounded-xl border border-zinc-100 w-full max-w-md p-8 shadow-none">
        <!-- Header -->
        <div class="flex items-start justify-between mb-6">
          <div>
            <div class="flex items-center gap-2 mb-1">
              <span class="w-0.5 h-3.5 rounded-full bg-primary flex-shrink-0" />
              <p class="text-xs font-semibold uppercase tracking-widest text-zinc-400">{{ copy.eyebrow }}</p>
            </div>
            <h2 class="text-xl font-semibold tracking-tight text-zinc-900">{{ copy.title }}</h2>
          </div>
          <button
            class="text-zinc-300 hover:text-zinc-500 transition-colors"
            @click="$emit('close')"
          >
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Usage indicator (CV-limit funnel only) -->
        <div v-if="feature === 'cv_limit'" class="mb-6 p-4 border border-zinc-100 rounded-xl">
          <div class="flex items-center justify-between mb-2">
            <span class="text-xs font-semibold uppercase tracking-widest text-zinc-400">CVs used</span>
            <span class="text-sm font-semibold text-zinc-900">{{ cvCount }} / {{ cvLimit }}</span>
          </div>
          <div class="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
            <div class="h-full bg-primary rounded-full transition-all" :style="{ width: '100%' }" />
          </div>
        </div>

        <!-- Benefits -->
        <p class="text-sm text-zinc-500 leading-relaxed mb-4">
          {{ copy.description }}
        </p>

        <ul class="space-y-2 mb-8">
          <li
            v-for="benefit in benefits"
            :key="benefit"
            class="flex items-center gap-2.5 text-sm text-zinc-600"
          >
            <svg class="w-4 h-4 text-primary flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            {{ benefit }}
          </li>
        </ul>

        <!-- Actions -->
        <div class="flex flex-col gap-3">
          <button
            class="w-full bg-primary text-white text-sm font-medium py-2.5 rounded-lg hover:opacity-90 transition-opacity"
            :disabled="loading"
            @click="handlePrimary"
          >
            <span v-if="loading">Redirecting to checkout…</span>
            <span v-else>{{ copy.primaryLabel }}</span>
          </button>
          <button
            class="w-full border border-zinc-100 text-zinc-600 text-sm font-medium py-2.5 rounded-lg hover:border-primary transition-colors"
            @click="$emit('close')"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useSubscription } from '../composables/useSubscription.js'

const props = defineProps({
  show: { type: Boolean, default: false },
  cvCount: { type: Number, default: 0 },
  cvLimit: { type: Number, default: 5 },
  // Which Pro gate triggered the modal: 'cv_limit' | 'ai_optimizer'
  feature: { type: String, default: 'cv_limit' },
  // Whether the user is signed in — guests must create an account before upgrading
  authenticated: { type: Boolean, default: true }
})

const emit = defineEmits(['close'])

const router = useRouter()
const { openCheckout } = useSubscription()
const loading = ref(false)

// Pro benefits, ordered so the funnel's headline feature leads the list.
const proBenefits = {
  ai: 'AI bullet optimizer — tailored to each job description',
  unlimited: 'Unlimited CVs for every application',
  references: 'Reference document attachments',
  mergedPdf: 'CV + references merged into one PDF',
  organise: 'Organise all your applications in one place',
  parser: 'CV parser — upload and auto-fill'
}

const benefits = computed(() => {
  if (props.feature === 'ai_optimizer') {
    return [
      proBenefits.ai,
      proBenefits.references,
      proBenefits.mergedPdf,
      proBenefits.unlimited,
      proBenefits.parser
    ]
  }
  return [
    proBenefits.unlimited,
    proBenefits.ai,
    proBenefits.references,
    proBenefits.mergedPdf,
    proBenefits.organise,
    proBenefits.parser
  ]
})

const copy = computed(() => {
  if (props.feature === 'ai_optimizer') {
    return {
      eyebrow: 'Pro feature',
      title: 'Optimize your CV with AI',
      description: props.authenticated
        ? 'The AI optimizer rewrites your bullet points for impact and tailors them to each job description. Available on the Pro plan.'
        : 'The AI optimizer rewrites your bullet points for impact and tailors them to each job description. Create a free account and upgrade to Pro to use it.',
      primaryLabel: props.authenticated ? 'Upgrade to Pro' : 'Create a free account'
    }
  }
  return {
    eyebrow: 'Free Plan',
    title: 'CV limit reached',
    description: 'Upgrade to Pro to create unlimited CVs and keep all your applications organised in one place.',
    primaryLabel: 'Upgrade to Pro'
  }
})

async function handlePrimary() {
  // Guests can't check out — route them to sign up first.
  if (!props.authenticated) {
    emit('close')
    router.push('/signup')
    return
  }

  loading.value = true
  try {
    await openCheckout()
  } finally {
    loading.value = false
  }
}
</script>
